import React, { useRef, useEffect, useState, useMemo } from 'react';
import Flickity from 'react-flickity-component';
import { PrismicRichText } from '@prismicio/react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import FadeIn from '@components/shared/FadeIn';
import LazyLoadImage from '@components/shared/LazyLoadImage';
import { BREAKPOINT } from '@constants';
import { PageSlice, SliceCarouselModuleDetails, SliceCarouselModuleItem } from '@contracts';
import IconArrow from '@images/IconArrow';
import LinkButton from '../LinkButton';
import { Gradient2Style } from '../SharedStyles';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';
import 'flickity/css/flickity.css';

interface Props {
  slice: PageSlice;
}
interface CarouselContentProps {
  item: SliceCarouselModuleItem;
  isNewContent: boolean;
}

const CarouselContent = ({ item, isNewContent }: CarouselContentProps) => (
  <ContentWrap $isNewContent={isNewContent}>
    <TextWrap>
      {item.overline && (
        <Typography $variant={TextVariant.UpperText3} $mobileVariant={TextVariant.UpperText4} as="h3">
          {item.overline}
        </Typography>
      )}
      {item.heading.richText.length > 0 && (
        <Heading>
          <PrismicRichText field={item.heading.richText} />
        </Heading>
      )}
      {item.subheading.richText.length > 0 && (
        <Subheading>
          <PrismicRichText field={item.subheading.richText} />
        </Subheading>
      )}
    </TextWrap>
    {item.button_url && (
      <LinkButton to={item.button_url} size="small" isInline>
        {item.button_text_key}
      </LinkButton>
    )}
  </ContentWrap>
);

const CarouselModule = ({ slice }: Props) => {
  const sliceDetails = slice.primary as SliceCarouselModuleDetails;
  const sliceItems = slice.items as SliceCarouselModuleItem[];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isNewContent, setIsNewContent] = useState(false);
  const flickityRef = useRef<Flickity | null>(null);

  const carouselOptions = useMemo(() => {
    return {
      setGallerySize: false,
      pageDots: false,
      prevNextButtons: false,
      cellAlign: 'left',
      watchCSS: false,
      adaptiveHeight: false,
      percentPosition: true,
      draggable: true,
      wrapAround: true
    };
  }, []);

  const triggerFadeAnimation = () => {
    setIsNewContent(true);
    setTimeout(() => setIsNewContent(false), 50);
  };

  const handleNavigation = (direction: 'next' | 'previous') => {
    if (direction === 'next') {
      flickityRef.current?.next();
    } else {
      flickityRef.current?.previous();
    }
    triggerFadeAnimation();
  };

  const handleSlideChange = () => {
    triggerFadeAnimation();
  };

  return (
    <FadeIn>
      <Wrapper $backgroundColor={sliceDetails.background_color} $reverseOrderMobile={sliceDetails.reverse_order_mobile} data-testid="wrapper">
        <ContentBlock>
          <CarouselContent isNewContent={isNewContent} item={sliceItems[currentIndex]} />
          <NavigationButtons>
            <NavigationButton onClick={() => handleNavigation('previous')}>
              <IconArrow style={{ transform: 'rotate(90deg) translateY(1px)' }} />
            </NavigationButton>
            <NavigationButton onClick={() => handleNavigation('next')}>
              <IconArrow style={{ transform: 'rotate(-90deg) translateY(1px)' }} />
            </NavigationButton>
          </NavigationButtons>
        </ContentBlock>
        <CarouselWrap>
          <Flickity
            className="carousel"
            elementType="div"
            options={carouselOptions}
            disableImagesLoaded={true}
            reloadOnUpdate={false}
            static={true}
            flickityRef={(carouselRef) => {
              flickityRef.current = carouselRef;
              carouselRef.on('change', () => {
                setCurrentIndex(carouselRef.selectedIndex);
                handleSlideChange();
              });
            }}
          >
            {useMemo(
              () => (
                <>
                  {sliceItems.map((item, idx) => (
                    <CarouselSlide key={idx} className="carousel-cell">
                      <ImageWrap>
                        <img src={item.image.url || ''} alt="" />
                      </ImageWrap>
                    </CarouselSlide>
                  ))}
                </>
              ),
              [sliceItems]
            )}
          </Flickity>
        </CarouselWrap>
      </Wrapper>
    </FadeIn>
  );
};

export default React.memo(CarouselModule);

interface WrapperProps {
  $backgroundColor: string | null;
  $reverseOrderMobile: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  background: ${(props) => props.$backgroundColor || Colors.cream};
  color: ${Colors.navyBlue};
  padding: 40px 0px;
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: row;
  column-gap: 72px;
  align-items: flex-start;
  justify-content: center;

  & > * {
    max-width: 600px;
  }

  .carousel {
    height: 100%;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    column-gap: 40px;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    padding: 40px 24px;
    flex-direction: ${(props) => (props.$reverseOrderMobile ? 'column-reverse' : 'column')};
    row-gap: 16px;

    & > * {
      max-width: none;
    }
  }
`;

const ContentBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0px 0px 0px 72px;
  flex: 0 0 50%;
  row-gap: 24px;
  min-height: 500px;
  position: relative;

  @media (max-width: ${BREAKPOINT.laptopM}px) {
    min-height: 400px;
    padding: 0px 0px 0px 100px;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    min-height: 150px;
    row-gap: 16px;
    padding: 0;
  }
`;

const ContentWrap = styled.div<{ $isNewContent: boolean }>`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  justify-content: center;
  flex: 1 0 100%;

  opacity: ${(props) => (props.$isNewContent ? 0 : 1)};
  transition: opacity 1s ease-in-out;
  /* If content is new, remove the transition for immediate hide */
  transition-duration: ${(props) => (props.$isNewContent ? '0s' : '1s')};

  @media (max-width: ${BREAKPOINT.tablet}px) {
    flex: 0;
  }
`;

const TextWrap = styled.div`
  row-gap: 16px;
  @media (max-width: ${BREAKPOINT.tablet}px) {
    row-gap: 8px;
  }
`;

const CarouselWrap = styled.div`
  flex: 0 0 50%;
  align-self: stretch;
  border-radius: 12px;
  overflow: hidden;
  max-height: 500px;

  .flickity-viewport {
    height: 100% !important;
  }

  @media (max-width: ${BREAKPOINT.laptop}px) {
    max-height: 320px;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    width: 100%;
    aspect-ratio: 1.1 / 1;
    max-height: 470px;
  }
`;

const CarouselSlide = styled.div`
  height: 100%;
  aspect-ratio: 1/1;
  border-radius: 20px;
  margin-right: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  color: ${Colors.navyBlue};
`;

const Heading = styled.div`
  & > * {
    ${TEXT_STYLES[TextVariant.Heading4]};
    color: inherit;

    @media (max-width: ${BREAKPOINT.tablet}px) {
      ${TEXT_STYLES[TextVariant.Heading5]};
    }

    @media (max-width: ${BREAKPOINT.mobileM}px) {
      ${TEXT_STYLES[TextVariant.Heading6]};
    }

    & > span.italic {
      font-style: italic;
    }

    & span.gradient2 {
      ${Gradient2Style}
    }
  }
`;

const Subheading = styled.div`
  max-width: 100%;
  margin: 8px 0 16px;

  & > * {
    ${TEXT_STYLES[TextVariant.BodyRegularLG]};
    color: inherit;

    & > span.bold {
      font-weight: 700;
    }

    @media (max-width: ${BREAKPOINT.mobileM}px) {
      ${TEXT_STYLES[TextVariant.BodyRegularLG]};
    }
  }
`;

const ImageWrap = styled.div`
  width: 100%;
  height: auto;
  aspect-ratio: 1/1;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-self: flex-end;
  flex-direction: row;
  column-gap: 16px;
  bottom: 0;

  @media (max-width: ${BREAKPOINT.tablet}px) {
    display: none;
  }
`;

const NavigationButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${Colors.paleGold};
  background-color: ${Colors.cream};
  padding: 0;

  &:hover {
    background-color: ${Colors.paleGold50};
  }
  > svg {
    margin: auto;
  }
`;
